import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {
    Alert, AlertTitle, Box, Breadcrumbs, Button, Chip, CircularProgress, Container, Link as MuiLink, Paper, Stack, Typography,
} from '@mui/material';
import ApiService from '../network/API';

const gradeColor = (g) => (g >= 5 ? 'success' : g >= 4 ? 'primary' : g >= 3 ? 'warning' : 'error');

const formatDue = (iso) =>
    new Date(iso).toLocaleString('ru-RU', {timeZone: 'Europe/Samara', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});

const dueText = (l) => {
    if (!l.closesAt) return 'срок не задан';
    return l.closed ? `срок истёк ${formatDue(l.closesAt)}` : `до ${formatDue(l.closesAt)}`;
};

// Страница экзамена студента: предмет, допуск (что сдать и до какого срока), подготовка и переход к самому экзамену.
const ExamOverviewPage = () => {
    const {eventId} = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        ApiService.getExamOverview(eventId)
            .then((d) => {
                setData(d);
                document.title = d.exam.title;
            })
            .catch((e) => setError(e?.reason || e?.message || 'Не удалось загрузить экзамен'));
    }, [eventId]);

    if (error) return <Container maxWidth="sm" sx={{py: 4}}><Alert severity="error">{error}</Alert></Container>;
    if (!data) return <Box display="flex" justifyContent="center" py={8}><CircularProgress/></Box>;

    const {exam, admission, prep} = data;
    const attempt = exam.attempt;
    const admitted = admission.configured && admission.status === 'ADMITTED';
    const active = exam.state === 'ACTIVE';

    return (
        <Container maxWidth="sm" sx={{py: 4}}>
            <Breadcrumbs sx={{mb: 2}}>
                <MuiLink component={Link} to="/exams" underline="hover" color="inherit">Экзамены</MuiLink>
                <Typography color="text.primary">{exam.title}</Typography>
            </Breadcrumbs>

            <Typography variant="h4" fontWeight={700} gutterBottom>{exam.title}</Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 3}} flexWrap="wrap" useFlexGap>
                <Typography color="text.secondary">Предмет: <b>{exam.tagName}</b> · {exam.durationMinutes} мин</Typography>
                <Button size="small" variant="outlined" onClick={() => navigate(`/tags/${exam.tagId}`)}>Открыть предмет</Button>
            </Stack>

            {/* Допуск */}
            {admission.configured ? (
                <Alert severity={admitted ? 'success' : 'warning'} sx={{mb: 2}}>
                    <AlertTitle>{admitted ? 'Вы допущены к экзамену' : 'Допуска к экзамену пока нет'}</AlertTitle>
                    <Typography variant="body2">
                        Сдано на 3 и выше: {admission.passed} из {admission.required} обязательных лекций.
                        {admission.manual === true && ' Допуск выдан преподавателем.'}
                        {admission.manual === false && ' Преподаватель пока не допускает вас к экзамену.'}
                    </Typography>
                    {admission.missing.length > 0 && (
                        <Box sx={{mt: 1}}>
                            <Typography variant="body2" fontWeight={600}>
                                {admitted ? 'Не сданы (допуск выдан вручную):' : 'Чтобы получить допуск, сдайте лекции минимум на 3:'}
                            </Typography>
                            {admission.missing.map((l) => (
                                <Box key={l.articleId} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <MuiLink component={Link} to={`/articles/${l.articleId}`} underline="hover">{l.title}</MuiLink>
                                    <Typography variant="body2" color={l.closed ? 'error' : 'text.secondary'} sx={{whiteSpace: 'nowrap'}}>
                                        {dueText(l)}
                                    </Typography>
                                </Box>
                            ))}
                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                                Время указано по самарскому времени.
                            </Typography>
                        </Box>
                    )}
                </Alert>
            ) : (
                <Alert severity="info" sx={{mb: 2}}>Для этого предмета допуск к экзамену не требуется.</Alert>
            )}

            {/* Подготовка */}
            {prep.length > 0 && (
                <Paper variant="outlined" sx={{p: 2, borderRadius: 2, mb: 2}}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Подготовка к экзамену</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>Эти лекции помогут подготовиться:</Typography>
                    <Stack>
                        {prep.map((l) => (
                            <MuiLink key={l.articleId} component={Link} to={`/articles/${l.articleId}`} underline="hover">{l.title}</MuiLink>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* Сам экзамен */}
            <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Экзамен</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {exam.state === 'NOT_STARTED' && 'Преподаватель откроет вам доступ и выдаст билет в начале экзамена.'}
                            {active && 'Экзамен идёт: билет выдан, время отсчитывается.'}
                            {exam.state === 'FINISHED' && 'Экзамен завершён.'}
                        </Typography>
                        {attempt?.comment && <Typography variant="body2" sx={{mt: 0.5}}>{attempt.comment}</Typography>}
                    </Box>
                    {attempt?.grade != null && <Chip color={gradeColor(attempt.grade)} label={`Оценка: ${attempt.grade}`} sx={{fontWeight: 700}}/>}
                </Stack>
                <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    sx={{mt: 2}}
                    disabled={!attempt}
                    onClick={() => navigate(`/exams/attempts/${attempt.id}`)}
                >
                    {active ? 'Перейти к экзамену' : attempt ? 'Открыть экзамен' : 'Экзамен ещё не открыт'}
                </Button>
            </Paper>
        </Container>
    );
};

export default ExamOverviewPage;
